import React from 'react'

import { CommercialOnly } from '@/components/common/CommercialOnly'
import { ActionItems } from '@/components/home/dashboard/ActionItems'
import { ActionItemStatistics } from '@/components/home/dashboard/ActionItemStatistics'
import { ActivePursuitStatistics } from '@/components/home/dashboard/ActivePursuitStatistics'
import { DashboardContextProvider } from '@/components/home/dashboard/context'
import { EstimatedValueStatistics } from '@/components/home/dashboard/EstimatedValueStatistics'
import { Header } from '@/components/home/dashboard/Header'
import { NewOpportunities } from '@/components/home/dashboard/NewOpportunities'
import {
  RecentlyViewedPursuits,
  RecentlyViewedPursuitsCompact,
} from '@/components/home/dashboard/RecentlyViewedPursuits'
import { UpsellFallback } from '@/components/home/dashboard/UpsellFallback'
import { UserTasksCalendar } from '@/components/home/dashboard/UserTasksCalendar'
import LoadingScreen from '@/components/loading/LoadingScreen'

import { useBillingRole } from '@/hooks/billing/useBillingRole'
import { useCalendarIsVisible } from '@/hooks/home/useCalendarToggle'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { usePermissionCheck } from '@/hooks/permissions/usePermissionCheck'

import type { UUID } from '@/types/Id'

// Things to add:
// - Industry news tailored to organization
// - Critical actions (e.g. suggested tasks based on news and spending insights)
//   - this will eventually be our bread and butter on the data side, need to get it right
// - Proposal metrics (win rate, efficiency, completion rate)

export const HomeDashboard: React.FC = () => {
  const isCalendarVisible = useCalendarIsVisible()
  const billingRole = useBillingRole()
  const { activeOrgId } = useActiveOrgId()
  const { data: hasReadAccess, isLoading: isReadPermissionLoading } =
    usePermissionCheck({
      resourceType: 'organization',
      resourceId: activeOrgId as UUID,
      permissions: ['read'],
      enabled: !!activeOrgId,
      select: (data) => data.get('read') === true,
    })

  const shouldShowGuestFallback =
    billingRole === 'Guest' && hasReadAccess === false
  const isLoading =
    !activeOrgId || billingRole === undefined || isReadPermissionLoading

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <DashboardContextProvider>
      <div className="flex flex-col h-full w-full bg-primary-50 dark:bg-default-dark h-full overflow-y-auto">
        <div className="w-full h-full overflow-y-auto">
          <main className="flex-grow mx-auto lg:px-8 py-3 lg:py-6 w-full h-full max-w-7xl">
            <div className="px-3 lg:px-0 w-full">
              <Header />
            </div>
            {!shouldShowGuestFallback && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6 mb-3 lg:mb-6">
                <EstimatedValueStatistics />
                <ActivePursuitStatistics period="month" />
                <ActionItemStatistics />
              </div>
            )}
            {!shouldShowGuestFallback && isCalendarVisible && (
              <>
                <div className="px-3 lg:px-0 mb-3 lg:mb-6">
                  <UserTasksCalendar />
                </div>
              </>
            )}
            {shouldShowGuestFallback ? (
              <>
                <UpsellFallback />
                <div className="col-span-3 lg:col-span-1 flex flex-col gap-3 lg:gap-6 pb-6">
                  <div className="hidden lg:block">
                    <RecentlyViewedPursuitsCompact />
                  </div>
                  <ActionItems />
                </div>
              </>
            ) : (
              <div className="grid lg:grid-cols-3 gap-3 lg:gap-6 pb-6 w-full">
                <div className="col-span-3 lg:col-span-2 space-y-3 lg:space-y-6 w-full">
                  <CommercialOnly>
                    <NewOpportunities />
                  </CommercialOnly>
                  <RecentlyViewedPursuits />
                </div>
                <div className="col-span-3 lg:col-span-1 flex flex-col gap-3 lg:gap-6 pb-6">
                  <CommercialOnly>
                    <div className="hidden lg:block">
                      <RecentlyViewedPursuitsCompact />
                    </div>
                  </CommercialOnly>
                  <ActionItems />
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </DashboardContextProvider>
  )
}
